import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useCustomSnackbar, CurrencyProvider } from '@procureai/ui-mui';
import { useEffect } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  IntakeQuoteFormData,
  intakeQuoteLineItemArraySchema,
  IntakeQuoteHeaderFormData,
  intakeQuoteHeaderFormDataDefault,
  intakeQuoteHeaderSchema,
} from '../../IntakeQuoteStep.schema';
import { IntakeQuoteHeaderForm } from '../IntakeQuoteHeaderForm/IntakeQuoteHeaderForm';
import { LineItemDefaultValues } from '../IntakeQuoteStepLineItemForm/IntakeQuoteStepLineItemForm';
import { IntakeQuoteStepLineItems } from '../IntakeQuoteStepLineItems/IntakeQuoteStepLineItems';
import { I18nNamespace } from '~/common/types';
import { useGridValidator } from '~/routes/IntakeDetail/components/IntakeDataGrid/IntakeDataGrid.hooks';

interface IntakeQuoteStepFormProps {
  formId: string;
  data: IntakeQuoteFormData;
  onSubmit: (data: IntakeQuoteFormData) => Promise<void>;
  disabled?: boolean;
  lineItemFormDefaultValues: LineItemDefaultValues;
}

export const IntakeQuoteStepForm = ({
  formId,
  data,
  onSubmit,
  disabled,
  lineItemFormDefaultValues,
}: IntakeQuoteStepFormProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeQuoteStep' });
  const { onError } = useCustomSnackbar();

  const apiRef = useGridApiRef();
  const { isValid } = useGridValidator({
    apiRef,
    schema: intakeQuoteLineItemArraySchema,
  });

  const form = useForm<IntakeQuoteHeaderFormData>({
    mode: 'onTouched',
    resolver: zodResolver(intakeQuoteHeaderSchema),
    defaultValues: intakeQuoteHeaderFormDataDefault,
  });

  useEffect(() => {
    form.reset(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const currency = useWatch({ control: form.control, name: 'currency' });

  const submit = (quoteDetails: IntakeQuoteHeaderFormData) => {
    const { success, data: lineItems } = isValid();
    if (!success) {
      onError(t('error.lineItems'));
      return;
    }
    onSubmit({ ...quoteDetails, lineItems });
  };

  return (
    <FormProvider {...form}>
      <form id={formId} onSubmit={form.handleSubmit(submit)} autoComplete="off" noValidate>
        <CurrencyProvider value={currency}>
          <Grid container spacing={4}>
            <Grid size={12}>
              <IntakeQuoteHeaderForm disabled={disabled} />
            </Grid>
            <Grid size={12}>
              <IntakeQuoteStepLineItems
                disabled={disabled}
                apiRef={apiRef}
                lineItems={data.lineItems}
                lineItemFormDefaultValues={lineItemFormDefaultValues}
              />
            </Grid>
          </Grid>
        </CurrencyProvider>
      </form>
    </FormProvider>
  );
};
